import { getToken, getRefreshToken, storeTokens, clearAuth } from "../auth";

// CSRF defence. Three layers, in order of importance:
//   1. Bearer token in localStorage (NOT a cookie) — cross-origin JS can't read it,
//      so an attacker page can't put it in the Authorization header. This alone
//      is what makes classic CSRF non-exploitable here.
//   2. CORS allow-list on the API rejects unknown origins on the preflight that
//      Authorization (a non-simple header) forces.
//   3. `X-Requested-With: XMLHttpRequest` — the API's AuthenticationFilter rejects
//      state-changing requests (POST/PUT/PATCH/DELETE) that don't carry this header
//      (see ERR_GL014). Cross-origin <form> posts can't set custom headers, and
//      fetch/XHR with this header forces a preflight. Both upload and JSON helpers
//      must send it; mobile/device clients are skip-listed by path prefix on the API.
const defaultHeaders = () => ({
  "Accept": "application/json",
  "Content-Type": "application/json",
  "X-Requested-With": "XMLHttpRequest"
});

const fileUploadHeaders = () => ({
  "Accept": "application/json",
  "X-Requested-With": "XMLHttpRequest"
});

// Merge caller-provided headers on top of the defaults, then inject the
// Bearer token if (a) one is stored and (b) the caller did not already set
// an Authorization header (currentUserApi passes a fresh token before it
// hits localStorage, so we honour an explicit override).
const withAuth = (base, headers) => {
  const merged = Object.assign({}, base, headers);
  const hasAuth = Object.keys(merged).some(k => k.toLowerCase() === "authorization");
  if (!hasAuth) {
    const token = getToken();
    if (token) merged.Authorization = "Bearer " + token;
  }
  return merged;
};

// 401 handling — refresh-on-401 with single-flight + drop to /login on failure.
//
// When the API rejects the bearer JWT (expired / revoked) we want to:
//   1. Try the refresh endpoint exactly once across however many requests 401'd
//      simultaneously (single-flight via the inFlightRefresh promise).
//   2. On refresh success: store the new tokens, retry the original request once.
//   3. On refresh failure (or no stored refresh token): clearAuth and redirect to
//      /login — the existing behaviour, just deferred until we know refresh is
//      hopeless.
//
// Skipping the redirect for `/login` and `/logout` avoids two failure modes:
//   - login screen: a 401 from a wrong-credential attempt would otherwise reload
//     the user away from the form they just submitted
//   - logout flow: the call that *causes* the 401 (revoking our own session)
//     shouldn't trigger an extra reload on top of the one logout already does
let sessionExpired = false;
let inFlightRefresh = null;
// One-shot guard for the force-change redirect: a page typically fires several
// requests at once, and an mcp=true JWT makes each return 403 ERR_GL023. Without
// this, every one of them calls window.location.assign, which thrashes the
// navigation and flashes a blank page. Reset naturally on the next full load.
let forcedChangeRedirect = false;

const onAuthExpired = () => {
  if (sessionExpired) return;
  sessionExpired = true;
  clearAuth();
  const path = window.location.pathname || "";
  if (!path.startsWith("/login") && !path.startsWith("/logout")) {
    window.location.assign("/login");
  }
};

// Lazy import to break the circular dep: loginApi imports apiHelper, apiHelper
// would import loginApi. Resolving it at call time keeps the module graph clean.
const refreshSession = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;
  if (inFlightRefresh) return inFlightRefresh;

  inFlightRefresh = (async () => {
    try {
      const { refreshSessionApi } = await import("./loginApi");
      const res = await refreshSessionApi(refreshToken);
      if (!res || res.status !== 200) return false;
      const body = await res.json();
      if (!body || !body.token) return false;
      storeTokens({
        token: body.token,
        tokenType: body.tokenType,
        refreshToken: body.refreshToken
      });
      return true;
    } catch {
      return false;
    } finally {
      inFlightRefresh = null;
    }
  })();
  return inFlightRefresh;
};

// Build the fetch promise from a normalised request. Used twice in the 401 path:
// once for the original request, once for the retry after refresh.
const buildFetch = (m, url, headers, body) => {
  if (m === "POST" && body instanceof FormData) {
    return fetch(url, { method: m, headers: withAuth(fileUploadHeaders(), headers), body });
  }
  if (m === "POST" || m === "PUT" || m === "PATCH") {
    return fetch(url, {
      method: m,
      headers: withAuth(defaultHeaders(), headers),
      body: JSON.stringify(body)
    });
  }
  if (m === "GET" || m === "DELETE") {
    return fetch(url, { method: m, headers: withAuth(defaultHeaders(), headers) });
  }
  throw new Error("please supply the required fields");
};

const handle401 = async (res, m, url, headers, body) => {
  // /authentication/* is the refresh + login path itself — a 401 here means the
  // refresh token is also dead, so there's nothing to do but drop to login.
  if (typeof url === "string" && url.includes("/authentication/")) {
    onAuthExpired();
    return res;
  }
  const refreshed = await refreshSession();
  if (!refreshed) {
    onAuthExpired();
    return res;
  }
  // Retry the original request exactly once with the refreshed token. If it
  // 401s again, give up — almost certainly a permission issue, not session.
  return buildFetch(m, url, headers, body);
};

// 403 ERR_GL023 means the backend's force-change guard is blocking the
// request — the JWT carries mcp=true. Defense-in-depth for the case where the
// SPA's local copy of the force-change flag has drifted (stale page,
// admin reset between login and next call). We patch localStorage so the Layout
// guard kicks in and route the user to the change-credential page.
const handle403 = async (res) => {
  try {
    // Body is read once — clone so the original res we hand back keeps a body
    // for the caller in case they want to read errorCode / message themselves.
    const body = await res.clone().json();
    if (body && body.errorCode === "ERR_GL023" && !forcedChangeRedirect) {
      forcedChangeRedirect = true;
      try {
        const stored = JSON.parse(localStorage.getItem("currentUser") || "{}");
        stored.mustChangePassword = true;
        localStorage.setItem("currentUser", JSON.stringify(stored));
      } catch (_) { /* localStorage write best-effort */ }
      if (typeof window !== "undefined" && window.location.pathname !== "/change-password") {
        window.location.assign("/change-password");
      }
    }
  } catch (_) {
    // Non-JSON body — not our error code, fall through.
  }
  return res;
};

const handleResponse = (responsePromise, m, url, headers, body) =>
  responsePromise.then(async (res) => {
    if (res && res.status === 401) {
      return handle401(res, m, url, headers, body);
    }
    if (res && res.status === 403) {
      return handle403(res);
    }
    return res;
  });

/**
 * Thin wrapper around fetch. Callers pass { url, method, headers, body };
 * the helper handles the shared concerns — default JSON headers, FormData
 * detection, Authorization injection, a global 401 interceptor, and a
 * single-flight refresh-and-retry on token expiry — so every *Api.js module
 * stays declarative.
 */
/* eslint-disable complexity */
const APIHelper = (fetchObj) => {
  if (!fetchObj || typeof fetchObj !== "object" || Object.keys(fetchObj).length === 0) {
    throw new Error("it is mandatory to pass request url and method");
  }
  const { url = null, method = null, headers = {}, body = {} } = fetchObj;
  if (!url || !method) {
    throw new Error("please supply the required fields");
  }
  const m = method.toUpperCase();
  return handleResponse(buildFetch(m, url, headers, body), m, url, headers, body);
};

export default APIHelper;
